import { useState } from 'react';

export interface TournamentExportData {
  version: string;
  tournament: {
    id: string;
    name: string;
    date: string;
    createdAt: string;
  };
  wrestlers: Array<{
    id: string;
    firstName: string;
    lastName: string;
    weightKg: number;
    age?: number;
    grade?: string;
    gender?: string;
    schoolName: string;
  }>;
  weightGroups?: Array<{
    name: string;
    wrestlers: string[]; // wrestler IDs
    minWeight: number;
    maxWeight: number;
    gender?: string;
  }>;
  drawState?: {
    weightDelta: number;
    separateGenders: boolean;
    adjustedWeightClasses: Array<{
      name: string;
      wrestlers: string[]; // wrestler IDs
      minWeight: number;
      maxWeight: number;
      gender?: string;
    }>;
    scheduledMatches?: Array<{
      boutNumber: number;
      wrestler1Id: string;
      wrestler2Id: string;
      mat: number;
      weightClass: string;
    }>;
    numMats?: number;
  };
}

export interface TournamentExportHookReturn {
  isExporting: boolean;
  isImporting: boolean;
  exportError: string | null;
  importError: string | null;
  exportSuccess: string | null;
  importSuccess: string | null;
  exportTournament: (data: TournamentExportData) => void;
  importTournament: (file: File) => Promise<TournamentExportData | null>;
  clearMessages: () => void;
}

export const useTournamentExport = (): TournamentExportHookReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  const clearMessages = () => {
    setExportError(null);
    setImportError(null);
    setExportSuccess(null);
    setImportSuccess(null);
  };

  const exportTournament = (data: TournamentExportData) => {
    setIsExporting(true);
    clearMessages();

    try {
      // Create export data with timestamp
      const exportData = {
        ...data,
        exportedAt: new Date().toISOString(),
        version: '1.0'
      };

      // Convert to JSON string
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create download URL
      const url = URL.createObjectURL(blob);
      
      // Create filename with tournament name and date
      const safeTournamentName = data.tournament.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `${safeTournamentName}_${data.tournament.date}_tournament.json`;
      
      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      URL.revokeObjectURL(url);
      
      setExportSuccess(`Tournament "${data.tournament.name}" exported successfully as ${filename}`);
    } catch (error) {
      console.error('Export error:', error);
      setExportError('Failed to export tournament. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const importTournament = async (file: File): Promise<TournamentExportData | null> => {
    setIsImporting(true);
    clearMessages();

    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const jsonString = event.target?.result as string;
          const data = JSON.parse(jsonString) as TournamentExportData;
          
          // Validate data structure
          if (!data.tournament || !data.tournament.name || !data.tournament.date) {
            throw new Error('Invalid tournament data structure');
          }
          
          if (!Array.isArray(data.wrestlers)) {
            throw new Error('Invalid wrestlers data');
          }
          
          // Check version compatibility
          if (data.version && data.version !== '1.0') {
            console.warn('Importing from different version:', data.version);
          }
          
          setImportSuccess(`Tournament "${data.tournament.name}" imported successfully`);
          resolve(data);
        } catch (error) {
          console.error('Import error:', error);
          setImportError('Failed to import tournament file. Please ensure it\'s a valid tournament export file.');
          resolve(null);
        } finally {
          setIsImporting(false);
        }
      };
      
      reader.onerror = () => {
        setImportError('Failed to read file. Please try again.');
        setIsImporting(false);
        resolve(null);
      };
      
      reader.readAsText(file);
    });
  };

  return {
    isExporting,
    isImporting,
    exportError,
    importError,
    exportSuccess,
    importSuccess,
    exportTournament,
    importTournament,
    clearMessages
  };
};
